// "Continuar con tu Tiendanube" — pantalla de aterrizaje del OAuth. Llega con ?claim=... (o ?error=1)
// desde tn-callback (rama continue). Llama a tn-continue-finish (crea/vincula la cuenta + devuelve el
// token de sesión), emite la sesión con verifyOtp (magiclink, sin contraseña), dispara el import de
// productos (best-effort) y entra a la app. Todo automático: el comerciante nunca se registra a mano.
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { ensureTnProducts, fireTnImport } from '../../lib/tiendaNube';
import { traerPorLaImportacionMasiva } from '../../lib/traerCatalogoAlConectar';

const TN_BATCH = 25;

export default function TiendanubeContinuar() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const ranRef = useRef(false); // el efecto corre una sola vez (evita doble ejecución en dev/StrictMode)

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    (async () => {
      const claim = params.get('claim');
      if (params.get('error') || !claim) {
        // Si el usuario YA tiene sesión (típico: volvió "atrás" desde los resultados y el navegador
        // re-ejecutó el OAuth con un code ya usado), el error es espurio → lo mandamos a la app.
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session) {
          navigate('/', { replace: true });
          return;
        }
        setError('No se pudo continuar con tu Tiendanube. Probá de nuevo.');
        return;
      }
      try {
        // 1. Completar el alta/login: crea o vincula la cuenta y devuelve el token de sesión.
        const { data, error: err } = await supabase.functions.invoke('tn-continue-finish', {
          body: { claim },
        });
        if (err) {
          let msg = 'No se pudo continuar con tu Tiendanube. Probá de nuevo.';
          try {
            const b = await (err as { context?: Response }).context?.json();
            if (b?.error) msg = b.error;
          } catch {
            /* sin cuerpo legible */
          }
          setError(msg);
          return;
        }
        const tokenHash = (data as { token_hash?: string } | null)?.token_hash;
        const esNuevo = (data as { es_nuevo?: boolean } | null)?.es_nuevo ?? false;
        // Marca donde tn-continue-finish vinculó la tienda (puede NO ser la default si ésa ya tenía otra
        // tienda). Se usa para traer/importar los productos ahí, no en la default.
        const brandDeClaim = (data as { brand_id?: string | null } | null)?.brand_id ?? null;
        if (!tokenHash) {
          setError('No se pudo iniciar sesión. Probá de nuevo.');
          return;
        }

        // 2. Emitir la sesión sin contraseña.
        const { error: otpErr } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'email' });
        if (otpErr) {
          setError('No se pudo iniciar sesión. Probá de nuevo.');
          return;
        }

        // 3. Importar los productos (la tienda ya quedó vinculada). Best-effort: si falla, entra igual.
        try {
          const {
            data: { user },
          } = await supabase.auth.getUser();
          const { products } = await ensureTnProducts(brandDeClaim);
          // La masiva se pide aunque la lectura de recién venga vacía: justo después de vincular puede dar 0 con
          // productos en la tienda (review Greptile #160). La lista sólo hace falta para el import de siempre.
          if (user) {
            const { data: prof } = await supabase
              .from('profiles')
              .select('id_company')
              .eq('id', user.id)
              .maybeSingle();
            const companyId = (prof as { id_company?: string | null } | null)?.id_company ?? null;
            // Importamos a la marca donde quedó la tienda (brandDeClaim); si no vino, a la default.
            let brandId = brandDeClaim;
            if (!brandId) {
              const { data: brand } = await supabase
                .from('brand_profiles')
                .select('id')
                .eq('id_company', companyId ?? '')
                .eq('is_default', true)
                .maybeSingle();
              brandId = (brand as { id?: string } | null)?.id ?? null;
            }
            // Primero la importación masiva (ADR-2026-10-05-conectar-tiendanube-por-la-importacion-masiva); si el
            // backend no la toma, el import de siempre.
            // El import de siempre: sólo si la masiva la rechaza enseguida (review Greptile #160).
            const deSiempre = async () => {
              if (products.length === 0) return;
              const payload = products.map((t) => ({
                nombre: t.nombre,
                imgs: t.imgs,
                tn_product_id: t.id,
                variantes: t.variantes,
              }));
              for (let i = 0; i < payload.length; i += TN_BATCH) {
                await fireTnImport({ userId: user.id, companyId, brandId, productos: payload.slice(i, i + TN_BATCH) });
              }
            };
            if (!(await traerPorLaImportacionMasiva(brandId))) await deSiempre();
          }
        } catch {
          /* el import es best-effort; el usuario entra a la app igual */
        }

        // 4. Usuario NUEVO → paso de generar la primera imagen; usuario que YA tenía cuenta → directo
        //    a la app (Interfaz V2), sin repetir el onboarding. `es_nuevo` lo decide tn-continue-finish.
        navigate(esNuevo ? '/tiendanube/producto' : '/', { replace: true });
      } catch {
        setError('No se pudo continuar con tu Tiendanube. Probá de nuevo.');
      }
    })();
  }, [params, navigate]);

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 bg-white px-5 text-center">
      {error ? (
        <>
          <p className="text-[15px] font-[600] leading-[20px] text-[#991B1B]">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/tiendanube', { replace: true })}
            className="rounded-[12px] bg-[#18181B] px-4 py-2.5 text-[14px] font-[700] leading-[19px] text-[#FAFAFA] transition-colors hover:bg-[#27272A]"
          >
            Volver
          </button>
        </>
      ) : (
        <>
          <Loader2 className="size-7 animate-spin text-brand-primary" />
          <p className="text-[15px] font-[600] leading-[20px] text-[#18181B]">Entrando con tu Tiendanube…</p>
          <p className="text-[13px] font-[500] leading-[18px] text-[#71717B]">
            Estamos preparando tu cuenta y tus productos.
          </p>
        </>
      )}
    </div>
  );
}
